import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import api from '../../services/api';
import { useNotification } from '../../context/NotificationContext';

interface SyncConfig {
  serverUrl: string | null;
  apiKeyConfigured: boolean;
  adminEmail: string | null;
  deviceId: string;
}

interface SyncStats {
  total: number;
  tables: Record<string, number>;
  lastPushAt: string | null;
  deviceId: string | null;
}

interface SyncEntry {
  id: number;
  tableName: string;
  recordId: number;
  action: string;
  field: string | null;
  createdAt: string;
  syncedAt: string | null;
}

export default function SyncTab() {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [config, setConfig] = useState<SyncConfig | null>(null);
  const [stats, setStats] = useState<SyncStats | null>(null);
  const [entries, setEntries] = useState<SyncEntry[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [pushResult, setPushResult] = useState<any>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configForm, setConfigForm] = useState({
    syncServerUrl: '',
    syncApiKey: '',
    syncAdminEmail: '',
    syncDeviceId: '',
  });

  const loadData = useCallback(async () => {
    try {
      const [configRes, statsRes, changelogRes] = await Promise.allSettled([
        api.get('/api/sync/config'),
        api.get('/api/sync/stats'),
        api.get('/api/sync/changelog?limit=20'),
      ]);

      if (configRes.status === 'fulfilled') setConfig(configRes.value.data);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (changelogRes.status === 'fulfilled') {
        setEntries(changelogRes.value.data.entries || []);
        setTotalEntries(changelogRes.value.data.total || 0);
      }
    } catch {
      // partial failure is OK
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handlePush = async () => {
    setPushing(true);
    setPushResult(null);
    try {
      const res = await api.post('/api/sync/push', {});
      setPushResult(res.data);
      if (res.data.pushed > 0) {
        showSuccess(`Синхронизировано: ${res.data.pushed} записей`);
      }
      loadData();
    } catch (err: any) {
      const data = err.response?.data;
      setPushResult(data || { error: err.message });
      if (data?.pushed) {
        showError(`Частичная ошибка: отправлено ${data.pushed} из ${data.count}, ${data.failedCount || 0} ошибок`);
      } else {
        showError(data?.error || 'Ошибка синхронизации');
      }
    } finally {
      setPushing(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      const updates: Array<Promise<any>> = [];
      if (configForm.syncServerUrl !== undefined) {
        updates.push(api.put('/api/admin/settings/syncServerUrl', { value: configForm.syncServerUrl }));
      }
      if (configForm.syncApiKey) {
        updates.push(api.put('/api/admin/settings/syncApiKey', { value: configForm.syncApiKey }));
      }
      if (configForm.syncAdminEmail !== undefined) {
        updates.push(api.put('/api/admin/settings/syncAdminEmail', { value: configForm.syncAdminEmail }));
      }
      if (configForm.syncDeviceId !== undefined) {
        updates.push(api.put('/api/admin/settings/syncDeviceId', { value: configForm.syncDeviceId }));
      }
      await Promise.all(updates);
      showSuccess('Настройки синхронизации сохранены');
      setConfigDialogOpen(false);
      loadData();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Ошибка сохранения');
    }
  };

  const formatTime = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const actionLabel = (action: string) => {
    switch (action) {
      case 'INSERT': return <Chip label="CREATE" color="success" size="small" />;
      case 'UPDATE': return <Chip label="UPDATE" color="info" size="small" />;
      case 'DELETE': return <Chip label="DELETE" color="error" size="small" />;
      default: return <Chip label={action} size="small" />;
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudSyncIcon /> Синхронизация с сервером
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setConfigForm({
                syncServerUrl: config?.serverUrl || '',
                syncApiKey: '',
                syncAdminEmail: config?.adminEmail || '',
                syncDeviceId: config?.deviceId || '',
              });
              setConfigDialogOpen(true);
            }}
          >
            Настройки
          </Button>
          <Button
            variant="contained"
            startIcon={pushing ? <CircularProgress size={18} /> : <SyncIcon />}
            onClick={handlePush}
            disabled={pushing || (stats?.total || 0) === 0}
            color={stats?.total ? 'primary' : 'inherit'}
          >
            {pushing ? 'Отправка...' : stats?.total ? `Синхронизировать (${stats.total})` : 'Всё синхронизировано'}
          </Button>
        </Box>
      </Box>

      {/* Status cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color={stats?.total ? 'warning.main' : 'success.main'}>
                {stats?.total || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">Ожидают синхронизации</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">Сервер</Typography>
              <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                {config?.serverUrl ? (
                  <>
                    <CheckCircleIcon color="success" sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                    {config.serverUrl}
                  </>
                ) : (
                  <>
                    <ErrorIcon color="warning" sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                    Не настроен
                  </>
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">Последняя отправка</Typography>
              <Typography variant="body1">{formatTime(stats?.lastPushAt)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">Устройство</Typography>
              <Typography variant="body1">{config?.deviceId || '—'}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Per-table breakdown */}
      {stats && Object.keys(stats.tables).length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Ожидают по таблицам:</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(stats.tables).map(([table, count]) => (
              <Chip key={table} label={`${table}: ${count}`} variant="outlined" size="small" />
            ))}
          </Box>
        </Box>
      )}

      {/* Push result */}
      {pushResult && (
        <Alert
          severity={pushResult.ok ? 'success' : 'warning'}
          sx={{ mb: 2 }}
          onClose={() => setPushResult(null)}
        >
          {pushResult.ok
            ? `Успешно синхронизировано: ${pushResult.pushed} записей`
            : pushResult.pushed
              ? `Частично: ${pushResult.pushed}/${pushResult.count} отправлено, ${pushResult.failedCount || 0} ошибок`
              : pushResult.error || 'Ошибка'}
        </Alert>
      )}

      {/* Changelog */}
      {entries.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Ожидающие изменения ({totalEntries})
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Таблица</TableCell>
                  <TableCell>Запись</TableCell>
                  <TableCell>Действие</TableCell>
                  <TableCell>Поле</TableCell>
                  <TableCell>Дата</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell>{e.id}</TableCell>
                    <TableCell>{e.tableName}</TableCell>
                    <TableCell>#{e.recordId}</TableCell>
                    <TableCell>{actionLabel(e.action)}</TableCell>
                    <TableCell>{e.field || '*'}</TableCell>
                    <TableCell>{formatTime(e.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Config dialog */}
      <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Настройки синхронизации</DialogTitle>
        <DialogContent>
          <TextField
            label="URL сервера"
            fullWidth
            margin="normal"
            placeholder="https://ftr.lilfil.ru"
            value={configForm.syncServerUrl}
            onChange={(e) => setConfigForm({ ...configForm, syncServerUrl: e.target.value })}
          />
          <TextField
            label="API ключ"
            fullWidth
            margin="normal"
            type="password"
            placeholder={config?.apiKeyConfigured ? '(ключ установлен)' : 'Введите ключ'}
            value={configForm.syncApiKey}
            onChange={(e) => setConfigForm({ ...configForm, syncApiKey: e.target.value })}
          />
          <TextField
            label="Email администратора"
            fullWidth
            margin="normal"
            placeholder="admin@ftr.ru"
            value={configForm.syncAdminEmail}
            onChange={(e) => setConfigForm({ ...configForm, syncAdminEmail: e.target.value })}
          />
          <TextField
            label="ID устройства"
            fullWidth
            margin="normal"
            placeholder="offline-device-1"
            value={configForm.syncDeviceId}
            onChange={(e) => setConfigForm({ ...configForm, syncDeviceId: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSaveConfig}>Сохранить</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}